import { IFormattedMMTrade } from "../hooks/api/mm/useMMTrades";

export interface IPnLRow {
  InstrumentName: string;
  Strategy: string;
  SnapshotStart: Date;
  SnapshotEnd: Date;
  TotalBuyVolume: number;
  TotalSellVolume: number;
  TotalBuyAmount: number;
  TotalSellAmount: number;
  TotalFees: number;
  AverageBuyPrice: number;
  AverageSellPrice: number;
  PnL: number;
  NetPnL: number;
}

export const processPnL = (
  trades: IFormattedMMTrade[],
  startTimestamp: number,
  endTimestamp: number,
  strategy: string = "MM"
): IPnLRow[] | null => {
  if (!trades || trades.length === 0) {
    return null;
  }

  const snapshotStart = new Date(startTimestamp);
  const snapshotEnd = new Date(endTimestamp);

  // eslint-disable-next-line no-console
  console.log("processPnL - Input trades length:", trades.length);
  // eslint-disable-next-line no-console
  console.log("processPnL - Time range:", {
    start: new Date(startTimestamp).toISOString(),
    end: new Date(endTimestamp).toISOString(),
    startTimestamp,
    endTimestamp,
  });

  // Filter trades by timestamp range (exclusive start, inclusive end to match Python SQL query)
  // Python query: WHERE _Timestamp > start AND _Timestamp <= end
  // Timestamp is in milliseconds for mm_trade_history
  const filteredTrades = trades.filter((trade) => {
    const tradeTimestamp = Number(trade.timestamp);
    if (Number.isNaN(tradeTimestamp)) {
      return false; // Skip trades with invalid timestamps
    }
    // Include trades where: startTimestamp < tradeTimestamp <= endTimestamp
    return tradeTimestamp > startTimestamp && tradeTimestamp <= endTimestamp;
  });

  // Trade IDs are only unique within a venue. Include the remaining API
  // identity fields so a Lighter fill cannot hide a fill from another venue.
  const uniqueTrades = filteredTrades.reduce((acc, trade) => {
    const tradeKey = [
      trade.instrument_name,
      trade.exchange,
      trade.trade_id,
      trade.side,
    ].join("\u0000");
    if (!acc.has(tradeKey)) {
      acc.set(tradeKey, trade);
    }
    return acc;
  }, new Map<string, IFormattedMMTrade>());

  const deduplicatedTrades = Array.from(uniqueTrades.values());

  // eslint-disable-next-line no-console
  console.log("processPnL - After deduplication length:", deduplicatedTrades.length);

  // eslint-disable-next-line no-console
  console.log("processPnL - After timestamp filter length:", filteredTrades.length);

  if (deduplicatedTrades.length === 0) {
    return null;
  }

  // Group by InstrumentName
  const grouped = deduplicatedTrades.reduce((acc, trade) => {
    const instrumentName = trade.instrument_name;
    if (!acc[instrumentName]) {
      acc[instrumentName] = [];
    }
    acc[instrumentName].push(trade);
    return acc;
  }, {} as Record<string, IFormattedMMTrade[]>);

  const pnlRows: IPnLRow[] = [];

  Object.entries(grouped).forEach(([instrumentName, instrumentTrades]) => {
    let totalBuyVolume = 0;
    let totalSellVolume = 0;
    let totalBuyAmount = 0;
    let totalSellAmount = 0;
    let totalFees = 0;

    instrumentTrades.forEach((trade) => {
      // Parse values and handle NaN (equivalent to fillna(0) in Python)
      const price = parseFloat(trade.price) || 0;
      const amount = parseFloat(trade.amount) || 0;
      const fees = parseFloat(trade.fees) || 0;
      const volume = price * amount;

      if (trade.side.toUpperCase() === "BUY") {
        totalBuyVolume += volume;
        totalBuyAmount += amount;
      } else if (trade.side.toUpperCase() === "SELL") {
        totalSellVolume += volume;
        totalSellAmount += amount;
      }

      totalFees += fees;
    });

    // Calculate averages - handle division by zero
    // AverageBuyPrice = TotalBuyVolume / TotalBuyAmount
    // AverageSellPrice = TotalSellVolume / TotalSellAmount
    const averageBuyPrice = totalBuyAmount > 0 ? totalBuyVolume / totalBuyAmount : 0;
    const averageSellPrice = totalSellAmount > 0 ? totalSellVolume / totalSellAmount : 0;

    // Calculate PnL
    // PnL = (AverageSellPrice - AverageBuyPrice) * min(TotalBuyAmount, TotalSellAmount)
    const minAmount = Math.min(totalBuyAmount, totalSellAmount);
    const pnl = minAmount > 0 ? (averageSellPrice - averageBuyPrice) * minAmount : 0;
    const netPnL = pnl - totalFees;

    pnlRows.push({
      InstrumentName: instrumentName,
      Strategy: strategy.toUpperCase(),
      SnapshotStart: snapshotStart,
      SnapshotEnd: snapshotEnd,
      TotalBuyVolume: roundTo6(totalBuyVolume),
      TotalSellVolume: roundTo6(totalSellVolume),
      TotalBuyAmount: roundTo6(totalBuyAmount),
      TotalSellAmount: roundTo6(totalSellAmount),
      TotalFees: roundTo6(totalFees),
      AverageBuyPrice: roundTo6(averageBuyPrice),
      AverageSellPrice: roundTo6(averageSellPrice),
      PnL: roundTo6(pnl),
      NetPnL: roundTo6(netPnL),
    });
  });

  // eslint-disable-next-line no-console
  console.log("processPnL - Final pnlRows length:", pnlRows.length);

  return pnlRows;
};

const roundTo6 = (value: number): number => Math.round(value * 1e6) / 1e6;
